import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User, LinkedAccount } from '@/types/user.types'

interface AuthState {
  user: User | null
  token: string | null
  linkedAccounts: LinkedAccount[]
  isAuthenticated: boolean
  impersonating: boolean
  originalUser: User | null
  originalToken: string | null
  _hasHydrated: boolean
  setHasHydrated: (state: boolean) => void
  setUser: (user: User) => void
  setToken: (token: string) => void
  setLinkedAccounts: (accounts: LinkedAccount[]) => void
  startImpersonation: (user: User, token: string) => void
  stopImpersonation: () => void
  clearAuth: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      linkedAccounts: [],
      isAuthenticated: false,
      impersonating: false,
      originalUser: null,
      originalToken: null,
      _hasHydrated: false,

      setHasHydrated: (state) => set({ _hasHydrated: state }),

      setUser: (user) => set({ user, isAuthenticated: true }),

      setToken: (token) => {
        // Save to both localStorage (for API interceptor) and state (for persistence)
        localStorage.setItem('auth_token', token)
        set({ token, isAuthenticated: true })
      },

      setLinkedAccounts: (accounts) => set({ linkedAccounts: accounts }),

      startImpersonation: (user, token) => set((state) => ({
        originalUser: state.user,
        originalToken: state.token,
        user,
        token,
        impersonating: true,
        isAuthenticated: true,
      })),

      stopImpersonation: () => set((state) => ({
        user: state.originalUser,
        token: state.originalToken,
        originalUser: null,
        originalToken: null,
        impersonating: false,
      })),

      clearAuth: () => {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('auth_user')
        set({
          user: null,
          token: null,
          linkedAccounts: [],
          isAuthenticated: false,
          impersonating: false,
          originalUser: null,
          originalToken: null,
        })
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,  // Include token in persisted state
        linkedAccounts: state.linkedAccounts,
        isAuthenticated: state.isAuthenticated,
        impersonating: state.impersonating,
        originalUser: state.originalUser,
        originalToken: state.originalToken,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Sync the persisted token to localStorage for API interceptor
          if (state.token) {
            localStorage.setItem('auth_token', state.token)
          }
          state.setHasHydrated(true)
        }
      }
    }
  )
)
